import { ButtonGroup, modals, Button, DataList } from '@cad-arc';
import { partition } from 'lodash';
import { FieldNameEnum, ReportingEventNumberDto } from '@mark43/cad-api';
import { useEnabledFeatures } from '../../../../../common/hooks/enabled-features/use-enabled-features';
import { observer } from 'mobx-react-lite';
import { useBootstrapState } from '../../../../../bootstrap/bootstrap-provider';
import { useEventMetadataStore } from '../../../../event-panel-form/event-metadata';
import { GenerateOtherAgencyReportingEventNumberModal } from './generate-other-agency-reporting-event-number-modal';
import { ReportingEventNumbersList } from './reporting-event-numbers-list';
import { useGenerateReportingEventNumber } from '../../../../../common/hooks/use-generate-reporting-event-number';
import { MergedEventNumbersList } from './merged-event-numbers-list';
import { useApplicationType } from '../../../../../common/hooks/use-application-type';

export const RenDetails = observer(function RenDetails({
    reportingEventNumbers,
    agencyId,
    eventId,
}: {
    reportingEventNumbers: ReportingEventNumberDto[];
    agencyId: number;
    eventId?: number;
}): React.ReactElement {
    const { multiRenGeneration } = useEnabledFeatures();
    const [generateReportingEventNumber] = useGenerateReportingEventNumber();
    const { isFirstResponder } = useApplicationType();

    const { fieldDisplayNameByFieldName: fieldDisplayNames } = useBootstrapState();

    const renDisplayName =
        fieldDisplayNames[FieldNameEnum.AGENCY_EVENT_REPORTING_EVENT_NUMBER.name];

    const eventMetadata = useEventMetadataStore().state.metadata;
    const agencyEventNumber = eventMetadata?.agencyEventNumber;
    const showGenerateRenPrimaryAgencyButton =
        !!eventMetadata?.showGenerateRenPrimaryAgencyButton && !isFirstResponder;
    const generateRenOtherAgencyAgencyProfileOptions =
        eventMetadata?.generateRenOtherAgencyAgencyProfileOptions || [];

    const showGenerateRenOtherAgencyButton =
        !!eventMetadata?.showGenerateRenOtherAgencyButton && !isFirstResponder;

    const [primaryReportingEventNumbers, otherReportingEventNumbers] = partition(
        reportingEventNumbers.filter((ren) => ren.agencyEventId === eventId),
        (reportingEventNumber) => {
            return reportingEventNumber.agencyId === agencyId;
        }
    );

    const primaryReportingEventNumbersToShow = multiRenGeneration
        ? primaryReportingEventNumbers.slice(0, 3)
        : primaryReportingEventNumbers;

    const otherReportingEventNumbersToShow = multiRenGeneration
        ? otherReportingEventNumbers.slice(0, 3)
        : otherReportingEventNumbers;

    const numberOfReportingEventNumbersShowing =
        primaryReportingEventNumbersToShow.length + otherReportingEventNumbersToShow.length;

    const showShowAllButton = reportingEventNumbers.length > numberOfReportingEventNumbersShowing;

    return (
        <>
            {(!!primaryReportingEventNumbersToShow.length ||
                !!otherReportingEventNumbersToShow.length) && (
                <DataList style={{ width: '100%' }}>
                    <ReportingEventNumbersList
                        reportingEventNumbers={primaryReportingEventNumbersToShow}
                    />
                    <ReportingEventNumbersList
                        reportingEventNumbers={otherReportingEventNumbersToShow}
                    />
                    <MergedEventNumbersList
                        reportingEventNumbers={reportingEventNumbers.filter(
                            (rn) => rn.agencyEventId !== eventId
                        )}
                    />
                </DataList>
            )}
            {(showShowAllButton ||
                showGenerateRenPrimaryAgencyButton ||
                showGenerateRenOtherAgencyButton) && (
                <ButtonGroup>
                    {showShowAllButton ? (
                        <Button
                            onClick={() => {
                                modals.alert({
                                    confirmLabel: 'Close',
                                    title: `${renDisplayName} for #${agencyEventNumber}`,
                                    body: (
                                        <DataList>
                                            <ReportingEventNumbersList
                                                reportingEventNumbers={reportingEventNumbers}
                                            />
                                        </DataList>
                                    ),
                                });
                            }}
                            variant="ghost"
                        >
                            Show All ({reportingEventNumbers.length})
                        </Button>
                    ) : undefined}
                    {showGenerateRenPrimaryAgencyButton && (
                        <Button
                            leadingVisual="Add"
                            variant="outline"
                            onClick={() => {
                                if (eventId && agencyEventNumber) {
                                    generateReportingEventNumber({
                                        agencyEventId: eventId,
                                        agencyEventNumber,
                                        numberOfRensForPrimaryAgency:
                                            primaryReportingEventNumbers.length,
                                    });
                                }
                            }}
                        >
                            Generate {renDisplayName}
                        </Button>
                    )}
                    {showGenerateRenOtherAgencyButton && (
                        <Button
                            leadingVisual="Add"
                            variant="outline"
                            onClick={() => {
                                if (eventId && agencyEventNumber) {
                                    return modals.blank({
                                        children: ({ onClose }) => (
                                            <GenerateOtherAgencyReportingEventNumberModal
                                                agencyProfileOptions={
                                                    generateRenOtherAgencyAgencyProfileOptions
                                                }
                                                agencyEventNumber={agencyEventNumber}
                                                eventId={eventId}
                                                onClose={onClose}
                                            />
                                        ),
                                        scrollBehavior: 'inside',
                                    });
                                }
                            }}
                        >
                            Other {renDisplayName}
                        </Button>
                    )}
                </ButtonGroup>
            )}
        </>
    );
});
